import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { TOOLS } from "@/content/tools";
import "./ToolsMenu.css";

/**
 * "Tools" dropdown for the main navigation, built as a disclosure: a button
 * with aria-expanded controlling a list of ordinary links. Links stay in the
 * DOM while closed (the `hidden` attribute), so prerendered HTML still carries
 * them for crawlers. Escape, clicking outside or tabbing away closes it.
 */
export default function ToolsMenu({ current }) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef(null);
  const button = useRef(null);
  const id = `tools-menu-${useId().replace(/:/g, "")}`;

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = event => { if (!wrapper.current?.contains(event.target)) setOpen(false); };
    const onKey = event => { if (event.key === "Escape") { setOpen(false); button.current?.focus(); } };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("pointerdown", onPointer); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const onBlur = event => { if (!wrapper.current?.contains(event.relatedTarget)) setOpen(false); };

  return <div className="ss-tools" ref={wrapper} onBlur={onBlur}>
    <button ref={button} type="button" className={`ss-tools-trigger${current ? " ss-tools-current" : ""}`} aria-expanded={open} aria-controls={id} onClick={() => setOpen(v => !v)} data-testid="nav-tools">
      Tools <ChevronDown size={16} aria-hidden="true" className="ss-tools-chevron" />
    </button>
    <ul id={id} className="ss-tools-panel" hidden={!open}>
      {TOOLS.map(tool => <li key={tool.href}>
        <a href={tool.href} aria-current={current === tool.href ? "page" : undefined} data-testid={`nav-tool-${tool.href.split("/").pop()}`}>
          <span className="ss-tools-name">{tool.name}{tool.note && <span className="ss-tools-note">{tool.note}</span>}</span>
          <span className="ss-tools-desc">{tool.description}</span>
        </a>
      </li>)}
    </ul>
  </div>;
}
